import React, { useCallback } from "react";

import moment from "moment";

import {
  formatCO2AmountRange,
  formatCurrencyRange,
  isSingleFieldOutcomes,
  useGetCarbonProgramByFarmId,
  useGetFarmReportingYear,
  useInventoryImpact,
  useTranslation,
} from "utils";

import { CURRENCY_CODE } from "consts";

import { CarbonProgramOutcomesResponse, SingleFieldOutcomes } from "models";

import { Button, Flex, FlexPageSpin, Modal } from "components";

import { formatPayoutsPostSubmission } from "pages/app/all-field-view/utils/AllFieldsUtils";

type SharedSubmissionProps = {
  carbonProgramOutcomes: CarbonProgramOutcomesResponse | null;
  farmId: string;
};

interface SubmissionSuccessModalProps extends SharedSubmissionProps {
  visible: boolean;
  onClose: () => void;
}

interface ModalContentProps extends SharedSubmissionProps {
  handleStartNextYear: () => void;
  variant?: "modal" | "panel";
}

export const SubmissionSuccessModalContent = ({ carbonProgramOutcomes, farmId, handleStartNextYear, variant = "modal" }: ModalContentProps) => {
  const { t } = useTranslation(["allFieldView"]);
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId ?? undefined);
  const { data: activeFarmReportingYear, isLoading: isFarmReportingYearLoading } = useGetFarmReportingYear(farmId ?? undefined);

  const targetYear = activeFarmReportingYear ?? moment().year();

  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(carbonProgramOutcomes ?? {}).filter(isSingleFieldOutcomes);
  const { totalPayoutMin, totalPayoutMax } = useInventoryImpact(targetYear, singleFieldOutcomes, farmId, carbonProgram ?? undefined);

  const submissionSuccessModalContent = formatPayoutsPostSubmission({
    carbonProgramOutcomes,
    carbonProgram: carbonProgram ?? null,
    targetYear,
    totalPayoutMin,
    totalPayoutMax,
  });

  const {
    totalPayoutMin: targetYearTotalPayoutMin,
    totalPayoutMax: targetYearTotalPayoutMax,
    totalCO2Min: targetYearTotalCO2Min,
    totalCO2Max: targetYearTotalCO2Max,
  } = submissionSuccessModalContent ?? {};

  const isModalVariant = variant === "modal";
  const currency = carbonProgram?.currency ?? CURRENCY_CODE.EUR;

  const showCO2SectionValues = false; // 🚨 TODO : remove this once the CO2 section issue is fixed

  if (isFarmReportingYearLoading) {
    return <FlexPageSpin />;
  }

  return (
    <Flex
      vertical
      align="center"
      gap={isModalVariant ? "1.5rem" : "1rem"}
      className={`${isModalVariant ? "padding-400" : "padding-100"} submission-success-modal-content`}
    >
      <Flex align="center" justify="center">
        <span style={{ fontSize: isModalVariant ? "4rem" : "3rem" }}>🎉</span>
      </Flex>

      <h1 className="margin-0 centered-text" style={{ fontSize: isModalVariant ? undefined : "1.5rem" }}>
        {t("submission.complete.title")}
      </h1>

      <Flex vertical align="center" className={"centered-text"} style={{ margin: isModalVariant ? undefined : "0 auto 1rem" }}>
        <p className={"margin-0 paragraph-l"}>{t("submission.complete.dataSubmitted")}</p>
        <p className={"margin-0 paragraph-l"}>{t("submission.complete.reportingYearSuccessfullySubmitted", { reportingYear: targetYear })}</p>
      </Flex>

      <Flex
        gap="0.625rem"
        style={{ width: "100%" }}
        vertical={!isModalVariant} // Stack vertically in panel mode
      >
        <div className="submission-outcome-card-container">
          <div className="submission-outcome-card">
            <p className={"margin-0 margin-bottom-50 text-center outcome-card-title"}>
              {formatCurrencyRange(targetYearTotalPayoutMin ?? 0, targetYearTotalPayoutMax ?? 0, { currency, decimals: 0 })}
            </p>
            <p className="paragraph-m margin-0">{t("submission.complete.totalExpectedPayout")}</p>
          </div>
        </div>

        {showCO2SectionValues && (
          <div className="submission-outcome-card-container">
            <div className="submission-outcome-card">
              <p className={`${isModalVariant ? "margin-0" : "margin-bottom-50"} text-center outcome-card-title`}>
                {formatCO2AmountRange(targetYearTotalCO2Min ?? 0, targetYearTotalCO2Max ?? 0)} CO<sub>2</sub>e
              </p>
              <p className="paragraph-m margin-0">{t("submission.complete.sequestratedInOurSoil")}</p>
            </div>
          </div>
        )}
      </Flex>

      <Flex vertical align="center" style={{ width: "100%" }}>
        <p className={"margin-0 paragraph-xl margin-bottom-50"}>{t("submission.complete.nextSteps")}</p>
        <p className={"paragraph-m  margin-0  next-steps-text "}>{t("submission.complete.nextStepsDescription")}</p>
      </Flex>

      {isModalVariant && (
        <Button type="primary" size="middle" onClick={handleStartNextYear} style={{ maxWidth: "23rem" }}>
          {t("submission.complete.startNextYear", { reportingYear: targetYear + 1 })}
        </Button>
      )}
    </Flex>
  );
};

export const SubmissionSuccessModal = ({ visible, onClose, carbonProgramOutcomes, farmId }: SubmissionSuccessModalProps) => {
  const handleStartNextYear = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      showModal={visible}
      footer={null}
      centered
      maskClosable
      onCancel={onClose}
      dataTestId="submission-success-modal"
      className="submission-success-modal"
      content={
        <SubmissionSuccessModalContent
          carbonProgramOutcomes={carbonProgramOutcomes}
          farmId={farmId}
          handleStartNextYear={handleStartNextYear}
          variant="modal"
        />
      }
    ></Modal>
  );
};
